import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProduccionService } from 'app/services/produccion.service';
import { ProductionRequirementsDialogComponent } from './production-requirements-dialog.component';
import { AlmacenService } from 'app/services/almacen.service';
import { RecetaEditCreateComponent } from '../recetas/receta-edit-create/receta-edit-create.component';
import { UmedidaService } from 'app/services/umedida.service';
import { MeasurementUnit } from 'app/models/umedida.models';
import { Subscription } from 'rxjs';
import { DialogManagerService } from 'app/services/dialog-manager.service';
import { EnhancedDialogService } from 'app/services/enhanced-dialog.service';

@Component({
  selector: 'app-orden-add-edit',
  templateUrl: './orden-add-edit.component.html',
  styleUrls: ['./orden-add-edit.component.css'],
})
export class OrdenAddEditComponent implements OnInit, OnDestroy {
  form: FormGroup;
  tipo: string;
  recetas: any[] = [];
  turnos: any[] = [];
  estadosProduccion: any[] = [];
  almacenes: any[] = []; // Array para almacenar los almacenes
  production: any;
  availableStatuses: any[];
  estadoActual: string;
  unidades: MeasurementUnit[] = [];

  // Objeto para rastrear campos completados
  fieldsStatus: { [key: string]: boolean } = {};

  private dialogSubscriptions: Subscription[] = [];
  private formSubscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<OrdenAddEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private produccionService: ProduccionService,
    private almacenService: AlmacenService,
    private dialog: MatDialog,
    private unidadService: UmedidaService, // inyectar servicio de unidades
    private enhancedDialog: EnhancedDialogService
  ) {
    this.tipo = data.tipo;
    this.production = data.production;
    this.availableStatuses = data.availableStatuses;
  }

  ngOnInit(): void {
    this.initForm();
    this.getParametros();
    this.loadAlmacenes();
    this.setLote();
    this.getUnidades(); // cargar unidades al iniciar
    // Suscribirse a cambios en el formulario
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    if (this.form) {
      this.form.reset();
    }
    this.recetas = [];
    this.turnos = [];
    this.estadosProduccion = [];
    this.almacenes = [];
    this.production = null;
    this.availableStatuses = [];
    this.estadoActual = '';
    this.unidades = [];
    this.fieldsStatus = {};

    // Desuscribir todas las suscripciones a afterClosed
    this.dialogSubscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.dialogSubscriptions = [];

    // Desuscribir todas las suscripciones a valueChanges del formulario
    this.formSubscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.formSubscriptions = [];

    // En lugar de cerrar todos los diálogos, limpiamos solo los overlays huérfanos
    // Esto evita cerrar diálogos que no pertenecen a este componente
    if (this.dialog && this.dialog.openDialogs.length > 0) {
      // Cerrar solo este diálogo si está abierto
      try {
        if (this.dialogRef) {
          this.dialogRef.close();
        }
      } catch (error) {
        console.error('Error al cerrar el diálogo:', error);
      }
    }
  }

  initForm() {
    this.form = this.fb.group({
      quantity: [null, [Validators.required, Validators.min(1)]],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      recipeId: ['', Validators.required],
      shiftId: ['', Validators.required],
      productionStatusId: ['', Validators.required],
      description: [''],
      batch: ['', Validators.required],
      warehouseId: ['', Validators.required], // Campo para almacén
      variantId: [{ value: '', disabled: true }], // Campo para variante (deshabilitado para edición)
    });
  }

  // Método para suscribirse a los cambios en el formulario
  subscribeToFormChanges() {
    // Inicializar el estado de los campos
    Object.keys(this.form.controls).forEach(key => {
      this.fieldsStatus[key] = !!this.form.get(key)?.value;
    });

    // Suscribirse a los cambios en cada control del formulario y guardar la suscripción
    Object.keys(this.form.controls).forEach(key => {
      const sub = this.form.get(key)?.valueChanges.subscribe(value => {
        this.fieldsStatus[key] = !!value;
      });
      if (sub) {
        this.formSubscriptions.push(sub);
      }
    });
  }

  // Método para verificar si un campo tiene valor
  isFieldFilled(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    if (!control) return false;
    
    // Para el campo de descripción, verificamos específicamente si tiene algún texto
    if (fieldName === 'description') {
      return control.value !== null && control.value !== undefined && control.value.trim() !== '';
    }
    
    return !!control.value;
  }

  loadAlmacenes() {
    this.almacenService.getAlmacenes().subscribe(
      (data: any) => {
        this.almacenes = data;
        // Si solo hay un almacén disponible, seleccionarlo automáticamente
        if (
          this.almacenes &&
          this.almacenes.length === 1 &&
          !this.form.get('warehouseId')?.value
        ) {
          this.form.get('warehouseId')?.setValue(this.almacenes[0].id);
        }
      },
      error => {
        console.error('Error al cargar almacenes:', error);
      }
    );
  }

  onRecipeSelected(event: any) {
    const recipeId = event.value;
    if (recipeId) {
      // Buscar la receta seleccionada para obtener el variantId
      const selectedRecipe = this.recetas.find(recipe => recipe.id === recipeId);
      if (selectedRecipe) {
        // Usar finalVariantId que es la propiedad correcta según el modelo de Recipes
        if (selectedRecipe.finalVariantId) {
          this.form.patchValue({
            variantId: selectedRecipe.finalVariantId,
          });
        }
        
        // Establecer directamente el yieldQuantity en el campo quantity
        if (selectedRecipe.yieldQuantity) {
          this.form.patchValue({
            quantity: selectedRecipe.yieldQuantity
          });
        }
      } else {
        // Si no se encuentra la variante o no está en la lista de recetas, cargar desde el servicio
        this.produccionService.getRecipe(recipeId).subscribe(
          (recipeData: any) => {
            if (recipeData) {
              if (recipeData.finalVariantId) {
                this.form.patchValue({
                  variantId: recipeData.finalVariantId,
                });
              }
              
              // Establecer directamente el yieldQuantity en el campo quantity
              if (recipeData.yieldQuantity) {
                this.form.patchValue({
                  quantity: recipeData.yieldQuantity
                });
              }
            }
          },
          error => {
            console.error('Error al obtener detalles de la receta:', error);
          }
        );
      }
    }
  }

  getParametros() {
    this.produccionService.getParametrosProduccion().subscribe(
      (data: any) => {
        this.recetas = data.recipes || []; 
        this.turnos = data.shifts || []; 
        this.estadosProduccion = this.production
          ? this.availableStatuses
          : data.productionStatus || []; 

        // Cuando se está creando una nueva orden (no hay producción existente)
        if (!this.production) {
          // Asignar directamente el ID 1 al estado PENDIENTE
          this.form.get('productionStatusId')?.setValue(1);
          // Deshabilitamos el campo para que no sea editable
          this.form.get('productionStatusId')?.disable();
        }

        if (data.batch) {
          this.form.get('batch')?.setValue(data.batch); 
        }
        if (this.production) {
          this.estadoActual = data.productionStatus.find(
            estado => estado.id === this.production.productionStatusId
          ).name;

          this.fillFormWithProductionData();

          this.configureFormControlsBasedOnStatus();

          this.tipo = 'Editar';
        }
      },
      (error: any) => {
        console.error('Error al obtener parámetros de producción:', error);
        this.showMessage('Error al obtener los parámetros de producción', 'error');
      }
    );
  }

  openRequirementsDialog(): void {
    const recipeId = this.form.get('recipeId')?.value;
    const quantity = this.form.get('quantity')?.value;

    if (!recipeId || !quantity) {
      this.showMessage('Debe seleccionar una receta y especificar la cantidad', 'error');
      return;
    }

    // Usar el servicio mejorado de diálogos que limpia overlays huérfanos automáticamente
    const sub = this.enhancedDialog.openDialog(ProductionRequirementsDialogComponent, {
      width: '50%',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        idRecipe: recipeId,
        quantity: quantity,
      },
    }).subscribe(result => {
      if (result) {
        console.log('Requerimientos de producción:', result);
        this.guardarOrden();
      }
    });
    
    // Registrar la suscripción para limpiarla al destruir el componente
    this.dialogSubscriptions.push(sub);
  }

  // Método auxiliar para mostrar mensajes con MatSnackBar
  showMessage(message: string, type: 'success' | 'error' = 'success'): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: type === 'error' ? ['error-snackbar'] : ['success-snackbar'],
    });
  }

  // Calcula el total CIF sumando los valores de los costos indirectos
  private calcularTotalCIF(costos: Array<{ nombre: string; valor: number }>): number {
    return costos.reduce((acc, c) => acc + Number(c.valor), 0);
  }

  guardarOrden() {
    if (this.form.valid) {
      const ordenData = this.form.getRawValue();
      ordenData.batch = this.form.get('batch')?.value;
      ordenData.quantity = Number(ordenData.quantity);

      this.produccionService.getCifData().subscribe((cif: any) => {
        const base = cif?.cif_base || 'unidad';
        const totalCIF = this.calcularTotalCIF(cif?.cif_costos || []);
        const tasaCIF = totalCIF && cif?.cif_volumen ? totalCIF / cif.cif_volumen : 0;
        let cantidadBase = ordenData.quantity;
        if (base === 'hora' && ordenData.horasProduccion) {
          cantidadBase = Number(ordenData.horasProduccion);
        }
        ordenData.allocatedIndirectCost = tasaCIF * cantidadBase;

        if (this.production) {
          this.produccionService.updateProduccion(this.production.id, ordenData).subscribe(
            (response: any) => {
              this.showMessage('Orden de producción actualizada correctamente');
              this.dialogRef.close(true);
            },
            (error: any) => {
              console.error('Error al actualizar la orden:', error);
              this.showMessage('Error al actualizar la orden', 'error');
            }
          );
        } else {
          this.produccionService.saveProduccion(ordenData).subscribe(
            (response: any) => {
              this.showMessage('Orden de producción guardada correctamente');
              this.dialogRef.close(true);
            },
            (error: any) => {
              console.error('Error al guardar la orden:', error);
              this.showMessage('Error al guardar la orden', 'error');
            }
          );
        }
      });
      return;
    } else {
      this.showMessage('Por favor, complete todos los campos requeridos', 'error');
      // Marcar todos los controles como tocados para mostrar los errores
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
    }
  }

  cancelar() {
    this.dialogRef.close(); // Cierra el diálogo sin guardar
  }

  setLote() {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0]; // Formato 'YYYY-MM-DD'

    // Solo establecer la fecha de inicio si no está ya establecida (como cuando se edita una orden)
    if (!this.form.get('startDate')?.value) {
      this.form.get('startDate')?.setValue(formattedDate);
    }

    // Siempre calculamos la fecha de vencimiento si no está establecida, usando la fecha de inicio como base
    if (!this.form.get('endDate')?.value) {
      // Obtenemos la fecha de inicio (ya sea la que acabamos de establecer o la que ya existía)
      const fechaInicio = this.form.get('startDate')?.value || formattedDate;
      const fechaInicioObj = new Date(fechaInicio);
      
      // Crear fecha de vencimiento sumando 1 año
      const fechaVencimiento = new Date(fechaInicioObj);
      fechaVencimiento.setFullYear(fechaVencimiento.getFullYear() + 1);
      
      // Formatear la fecha de vencimiento al formato YYYY-MM-DD
      const fechaVencimientoFormateada = fechaVencimiento.toISOString().split('T')[0];
      
      this.form.get('endDate')?.setValue(fechaVencimientoFormateada);
    }
  }

  actualizarFechaVencimiento() {
    const fechaInicio = this.form.get('startDate')?.value;
    
    if (fechaInicio) {
      // Crear una fecha a partir del valor del formulario
      const fechaInicioObj = new Date(fechaInicio);
      
      // Crear una nueva fecha sumando 1 año
      const fechaVencimiento = new Date(fechaInicioObj);
      fechaVencimiento.setFullYear(fechaVencimiento.getFullYear() + 1);
      
      // Formatear la fecha de vencimiento al formato YYYY-MM-DD
      const fechaFormateada = fechaVencimiento.toISOString().split('T')[0];
      
      // Establecer la fecha de vencimiento en el formulario
      this.form.get('endDate')?.setValue(fechaFormateada);
    }
  }

  private fillFormWithProductionData(): void {
    this.form.patchValue({
      recipeId: this.production.recipeId,
      shiftId: this.production.shiftId,
      productionStatusId: this.production.productionStatusId,
      quantity: this.production.quantity,
      description: this.production.description || '',
      warehouseId: this.production.warehouseId,
      batch: this.production.batch || '',
    });

    this.handleVariantId();
    this.handleDates();
  }

  private handleVariantId(): void {
    if (this.production.variantId) {
      this.form.patchValue({ variantId: this.production.variantId });
    } else if (this.production.recipeId) {
      const selectedRecipe = this.recetas.find(
        recipe => recipe.id === this.production.recipeId
      );

      if (selectedRecipe?.finalVariantId) {
        this.form.patchValue({ variantId: selectedRecipe.finalVariantId });
      } else {
        this.fetchRecipeDetails();
      }
    }
  }
  private fetchRecipeDetails(): void {
    this.produccionService.getRecipe(this.production.recipeId).subscribe(
      (recipeData: any) => {
        if (recipeData?.finalVariantId) {
          this.form.patchValue({ variantId: recipeData.finalVariantId });
        }
      },
      error => console.error('Error al obtener detalles de la receta:', error)
    );
  }
  private handleDates(): void {
    try {
      const startDate = this.production.startDate
        ? new Date(this.production.startDate)
        : null;
      const endDate = this.production.endDate ? new Date(this.production.endDate) : null;

      if (startDate && !isNaN(startDate.getTime())) {
        this.form.get('startDate')?.setValue(startDate.toISOString().split('T')[0]);
      }

      if (endDate && !isNaN(endDate.getTime())) {
        this.form.get('endDate')?.setValue(endDate.toISOString().split('T')[0]);
      }
    } catch (error) {
      console.error('Error al convertir fechas:', error);
    }
  }

  private configureFormControlsBasedOnStatus(): void {
    const isPending = this.estadoActual?.toUpperCase() === 'PENDIENTE';

    // Campos que solo se pueden modificar si está PENDIENTE
    const pendingOnlyFields = ['recipeId', 'quantity', 'warehouseId', 'batch'];

    // Campos que siempre están habilitados (excepto cuando no es PENDIENTE)
    const alwaysEnabledFields = [
      'startDate',
      'endDate',
      'shiftId',
      'productionStatusId',
      'description',
    ];

    // Configuramos todos los controles
    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.get(controlName);

      if (isPending) {
        // Si es PENDIENTE, habilitamos todos los campos
        control?.enable();
      } else {
        // Si NO es PENDIENTE
        if (pendingOnlyFields.includes(controlName)) {
          control?.disable();
        } else if (alwaysEnabledFields.includes(controlName)) {
          control?.enable();
        } else {
          control?.disable();
        }
      }
    });

    // Configuramos las opciones disponibles para el estado
  }

  getParametrosOld() {
    this.produccionService.getParametrosProduccion().subscribe(
      (data: any) => {
        this.recetas = data.recipes || []; // Asigna los datos de recetas a la variable recetas
        this.turnos = data.shifts || []; // Asigna los datos de turnos a la variable turnos
        this.estadosProduccion = this.production
          ? this.availableStatuses
          : data.productionStatus || []; // Asigna los datos de estados de producción a la variable estadosProduccion

        if (data.batch) {
          this.form.get('batch')?.setValue(data.batch); // Asigna el lote si existe
        }

        if (this.production) {
          this.estadoActual = data.productionStatus.find(
            estado => estado.id === this.production.productionStatusId
          ).name!;
          // Si existe una producción, asigna los valores al formulario
          this.form.get('recipeId')?.setValue(this.production.recipeId); // Asigna el id de receta al formulario
          this.form.get('shiftId')?.setValue(this.production.shiftId); // Asigna el id de turno al formulario
          this.form
            .get('productionStatusId')
            ?.setValue(this.production.productionStatusId); // Asigna el id de estado de producción al formulario
          this.form.get('quantity')?.setValue(this.production.quantity); // Asigna la cantidad al formulario
          this.form.get('description')?.setValue(this.production.description || ''); // Asigna la descripción al formulario
          this.form.get('warehouseId')?.setValue(this.production.warehouseId); // Asigna el almacén al formulario
          this.form.get('batch')?.setValue(this.production.batch || ''); // Asigna el lote al formulario

          // Asignar el variantId si existe
          if (this.production.variantId) {
            this.form.patchValue({
              variantId: this.production.variantId,
            });
          } else if (this.production.recipeId) {
            // Si no tiene variantId pero sí tiene recipeId, obtener la variante de la receta
            const selectedRecipe = this.recetas.find(
              recipe => recipe.id === this.production.recipeId
            );
            if (selectedRecipe && selectedRecipe.finalVariantId) {
              this.form.patchValue({
                variantId: selectedRecipe.finalVariantId,
              });
            } else {
              // Si la receta no tiene finalVariantId en la lista cargada, obtenerla directamente
              this.produccionService.getRecipe(this.production.recipeId).subscribe(
                (recipeData: any) => {
                  if (recipeData && recipeData.finalVariantId) {
                    this.form.patchValue({
                      variantId: recipeData.finalVariantId,
                    });
                  }
                },
                error => {
                  console.error('Error al obtener detalles de la receta:', error);
                }
              );
            }
          }

          // Asegurarse de que startDate y endDate sean objetos Date
          let startDate = null;
          let endDate = null;

          try {
            startDate = this.production.startDate
              ? new Date(this.production.startDate)
              : null;
            endDate = this.production.endDate ? new Date(this.production.endDate) : null;
          } catch (error) {
            console.error('Error al convertir fechas:', error);
          }

          // Formatear las fechas si son válidas
          if (startDate && !isNaN(startDate.getTime())) {
            const formattedDateStart = startDate.toISOString().split('T')[0]; // Formato 'YYYY-MM-DD'
            this.form.get('startDate')?.setValue(formattedDateStart);
          }

          if (endDate && !isNaN(endDate.getTime())) {
            const formattedDateEnd = endDate.toISOString().split('T')[0]; // Formato 'YYYY-MM-DD'
            this.form.get('endDate')?.setValue(formattedDateEnd);
          }

          this.tipo = 'Editar'; // Cambia el tipo a 'Editar' si existe una producción
        }
      },
      (error: any) => {
        console.error('Error al obtener parámetros de producción:', error);
        this.showMessage('Error al obtener los parámetros de producción', 'error');
      }
    );
  }

  // Método para abrir el diálogo de creación de receta desde la orden
  abrirDialogoReceta() {
    // Crear siempre un objeto de datos limpio para evitar referencias a datos antiguos
    const dialogData = {
      unidades: this.unidades ? [...this.unidades] : [],
      receta: null,
      onlyReadRecipe: false
    };
    
    // Usar el servicio mejorado para abrir diálogos
    const sub = this.enhancedDialog.openDialog(RecetaEditCreateComponent, {
      width: '60%',
      disableClose: true,
      autoFocus: true, // Asegurar que el diálogo reciba el foco
      panelClass: 'receta-dialog', // Clase para poder identificar este diálogo específico en el DOM
      data: dialogData,
    }).subscribe(result => {
      if (result) {
        this.getParametros();
      }
    });
    
    this.dialogSubscriptions.push(sub);
  }

  getUnidades() {
    this.unidadService.getUmedidas().subscribe(
      (data: MeasurementUnit[]) => {
        this.unidades = data;
      },
      error => {
        console.error(error);
      }
    );
  }
}
